// clients/web/src/app/services/upload-errors.ts
export type UploadErrorCode =
  // Pre-upload validation (gateway, 400)
  | 'UNSUPPORTED_FILE_TYPE'
  | 'BLOCKED_FILE_TYPE'
  | 'FILE_TOO_LARGE'
  | 'PERMISSION_DENIED'
  | 'AFFIRMATION_REQUIRED'
  | 'QUOTA_EXCEEDED'
  // Client-side pre-flight (frontend-only)
  | 'CORRUPT_ARCHIVE'
  // Upload transport (frontend → R2, frontend-only)
  | 'PRESIGN_FAILED'
  | 'CORS_REJECTED'
  | 'SIGNATURE_MISMATCH'
  | 'UPLOAD_STALLED'
  | 'NETWORK_ERROR'
  | 'UPLOAD_CANCELLED'
  // Post-upload (content service)
  | 'UPLOAD_INCOMPLETE'
  | 'PROCESSING_FAILED'
  // Archive-specific (content service — NOT failures; content still usable)
  | 'MANIFEST_TRUNCATED'
  | 'MANIFEST_FAILED';

export class UploadError extends Error {
  constructor(
    public readonly code: UploadErrorCode,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

export const UPLOAD_ERROR_MESSAGES: Record<UploadErrorCode, string> = {
  UNSUPPORTED_FILE_TYPE: "This file type isn't supported.",
  BLOCKED_FILE_TYPE: "Executable and script files aren't allowed for security reasons.",
  FILE_TOO_LARGE: "This file is larger than the size limit for your plan.",
  PERMISSION_DENIED: "You don't have permission to upload to this team.",
  AFFIRMATION_REQUIRED: "Please confirm the upload affirmation before continuing.",
  QUOTA_EXCEEDED: "Your organization is out of storage capacity.",
  CORRUPT_ARCHIVE: "This archive file appears to be corrupted or isn't a valid archive of the declared type. Try uploading a different file.",
  PRESIGN_FAILED: "Couldn't start the upload. Please try again.",
  CORS_REJECTED: "The server rejected the upload (CORS policy). Contact support if this persists.",
  SIGNATURE_MISMATCH: "File-type mismatch between browser and server. Try re-selecting the file.",
  UPLOAD_STALLED: "The upload stalled — no progress for 30 seconds. Click retry.",
  NETWORK_ERROR: "Upload couldn't reach the storage server. This is usually a CORS, firewall, or browser-extension issue — not necessarily your network. Open browser DevTools → Network tab and retry to see what's actually failing.",
  UPLOAD_CANCELLED: "Upload cancelled.",
  UPLOAD_INCOMPLETE: "The upload didn't finish. Please try again.",
  PROCESSING_FAILED: "Something went wrong processing this file after upload.",
  MANIFEST_TRUNCATED: "Archive contents partially listed — exceeded inspection limits.",
  MANIFEST_FAILED: "Couldn't read the contents of this archive. The file is still downloadable.",
};

const RETRYABLE_CODES: ReadonlySet<UploadErrorCode> = new Set<UploadErrorCode>([
  'PRESIGN_FAILED',
  'CORS_REJECTED',
  'SIGNATURE_MISMATCH',
  'UPLOAD_STALLED',
  'NETWORK_ERROR',
]);

export function isRetryable(code: UploadErrorCode): boolean {
  return RETRYABLE_CODES.has(code);
}
